import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useProductTour } from '../../contexts/ProductTourContext';
import ProductTourProgress from './ProductTourProgress';

const ProductTourCard: React.FC = () => {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    nextStep,
    previousStep,
    skipTour,
    endTour,
  } = useProductTour();

  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Fade-in on step changes
  useEffect(() => {
    if (isActive && currentStep) {
      setVisible(false);
      setAnimating(true);
      const timer = setTimeout(() => {
        setVisible(true);
        setAnimating(false);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [isActive, currentStep?.id]);

  if (!isActive || !currentStep) return null;

  const isLastStep = currentStepIndex === totalSteps - 1;
  const isFirstStep = currentStepIndex === 0;
  const StepIcon = currentStep.icon;

  return (
    <div
      className={`fixed bottom-6 right-6 z-[1500] w-[420px] max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl border border-gray-200 bg-white overflow-hidden transition-all duration-300 ${
        visible && !animating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      {/* Header */}
      <div
        className="px-5 py-4 flex items-center justify-between"
        style={{ background: 'linear-gradient(135deg, #42389D, #5B4FC7)' }}
      >
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
            <StepIcon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-white font-semibold text-base">{currentStep.title}</h3>
        </div>
        <button
          onClick={skipTour}
          className="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
          aria-label="Close tour"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        <p className="text-gray-600 text-sm leading-relaxed mb-3">
          {currentStep.description}
        </p>
        <ul className="space-y-2">
          {currentStep.highlights.map((highlight, i) => (
            <li key={i} className="flex items-start space-x-2 text-sm text-gray-700">
              <Sparkles className="w-3.5 h-3.5 text-purple-500 mt-0.5 flex-shrink-0" />
              <span>{highlight}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Progress */}
      <div className="px-5">
        <ProductTourProgress current={currentStepIndex} total={totalSteps} />
      </div>

      {/* Footer */}
      <div className="px-5 py-3 flex items-center justify-between border-t border-gray-100 mt-3">
        <button
          onClick={skipTour}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          Skip tour
        </button>
        <div className="flex items-center space-x-2">
          {!isFirstStep && (
            <button
              onClick={previousStep}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>Back</span>
            </button>
          )}
          <button
            onClick={isLastStep ? endTour : nextStep}
            className="flex items-center space-x-1 px-4 py-1.5 text-sm text-white rounded-lg font-medium transition-colors"
            style={{ background: 'linear-gradient(135deg, #42389D, #5B4FC7)' }}
          >
            <span>{isLastStep ? 'Finish' : 'Next'}</span>
            {!isLastStep && <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductTourCard;
